import React, { useState, useEffect } from 'react';
import { Dialog } from "primereact/dialog";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Spinner from "components/common/Spinner";
import { fetchWithToken } from "helpers/fetch";
import { endPoints } from "const/endPoints";

export const PreviousResultsModal = ({ quizId, visible, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (visible && quizId) {
      fetchResults();
    }
  }, [visible, quizId]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await fetchWithToken(
        `${endPoints.get_quiz_results}/${quizId}`
      );
      
      if (response.status === 200) {
        const data = await response.json();
        setResults(data);
      } else {
        console.error('Error fetching results:', response);
      }
    } catch (error) {
      console.error('Error fetching quiz results:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      header="Resultados del Último Intento"
      visible={visible}
      style={{ width: "60vw", height: "70vh" }}
      onHide={onClose}
      modal={true}
    >
      <Box sx={{ width: "100%", padding: "1rem" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <Spinner />
          </Box>
        ) : results ? (
          <>
            {/* Header with overall results */}
            <Box sx={{ textAlign: "center", marginBottom: "2rem" }}>
              {results.status === "APROBADO" ? (
                <CheckCircleOutlineIcon sx={{ color: "#4dc95d", fontSize: "4rem" }} />
              ) : (
                <HighlightOffIcon sx={{ color: "#dc3545", fontSize: "4rem" }} />
              )}
              
              <Typography variant="h5" sx={{ fontWeight: "bold", marginTop: "1rem" }}>
                {results.status === "APROBADO" ? "¡Felicidades! Has aprobado" : "Has reprobado, vuelve a intentarlo luego."}
              </Typography>
              
              <Typography variant="h6" sx={{ marginTop: "1rem", color: "#495057" }}>
                Puntaje: {results.score}/20 (obtuviste {results.earnedPoints} de {results.totalPoints} puntos)
              </Typography>

              <Typography variant="body2" sx={{ marginTop: "0.5rem", color: "#666" }}>
                Intento #{results.attemptNumber} - {new Date(results.attemptDate).toLocaleDateString()}
              </Typography>
            </Box>

            {/* Detailed results per question */}
            {results.detailsAvailable === false ? (
              // Los intentos hechos antes de que se guardaran las respuestas no
              // tienen detalle. Decirlo en vez de pintar todo como incorrecto.
              <Box
                sx={{
                  padding: "1rem",
                  borderRadius: "8px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffeeba",
                }}
              >
                <Typography variant="body2" sx={{ color: "#856404" }}>
                  El detalle pregunta por pregunta no está disponible para este
                  intento, porque se realizó antes de que el sistema guardara
                  las respuestas. La nota mostrada arriba sí es la correcta.
                </Typography>
              </Box>
            ) : (
            <>
            <Typography variant="h6" sx={{ marginBottom: "1rem", fontWeight: "bold" }}>
              Detalles por pregunta:
            </Typography>
            
            {results.results.map((result, index) => (
              <Accordion key={index} sx={{ marginBottom: "0.5rem" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: result.isCorrect ? "#d4edda" : "#f8d7da",
                    border: `1px solid ${result.isCorrect ? "#c3e6cb" : "#f5c6cb"}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Typography sx={{ flex: 1, fontSize: "0.9rem" }}>
                      Pregunta {index + 1}: {result.isCorrect ? "Correcta" : "Incorrecta"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
                      {result.earnedPoints} de {result.points} puntos
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                      {result.question}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                      <strong>Tu respuesta:</strong> {result.selectedAnswer}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ marginBottom: "0.5rem", color: "#28a745" }}>
                      <strong>Respuesta correcta:</strong> {result.correctAnswer}
                    </Typography>
                    
                    {result.explanation && (
                      <Box sx={{ 
                        marginTop: "1rem", 
                        padding: "1rem", 
                        backgroundColor: "#f8f9fa", 
                        borderLeft: "4px solid #007bff",
                        borderRadius: "4px" 
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                          💡 Explicación:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#495057" }}>
                          {result.explanation}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
            </>
            )}

            <Box sx={{ textAlign: "center", marginTop: "2rem" }}>
              <Button variant="contained" onClick={onClose}>
                Cerrar
              </Button>
            </Box>
          </>
        ) : (
          <Typography>No se pudieron cargar los resultados</Typography>
        )}
      </Box>
    </Dialog>
  );
};